// <s-product-gallery> — one large media slide at a time with a thumbnail strip (larger screens)
// or prev/next arrows and a "1 / N" counter (mobile, where the next photo peeks in).
// Thumbnails ([data-gallery-thumb]) and arrows ([data-gallery-prev]/[data-gallery-next]) scroll the
// track ([data-product-gallery]); swiping keeps the current thumbnail and counter in sync. One component per file.
class SProductGallery extends HTMLElement {
  #ac = null;
  #frame = 0;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.querySelector('[data-product-gallery]');
    if (!track) return;

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (thumb && this.contains(thumb)) {
        this.#goTo(track, Number(thumb.dataset.galleryThumb));
        return;
      }
      const total = this.#slides(track).length;
      if (event.target.closest('[data-gallery-prev]')) {
        this.#goTo(track, (this.#index - 1 + total) % total);
      } else if (event.target.closest('[data-gallery-next]')) {
        this.#goTo(track, (this.#index + 1) % total);
      }
    }, opts);

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync(track));
    }, { ...opts, passive: true });

    this.#sync(track);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #slides(track) {
    return [...track.children];
  }

  #offset(track, slide) {
    const padding = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
    return slide.offsetLeft - track.offsetLeft - padding;
  }

  #goTo(track, index) {
    const slide = this.#slides(track)[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: this.#offset(track, slide), behavior: reduce ? 'auto' : 'smooth' });
    this.#mark(index, this.#slides(track).length);
  }

  #sync(track) {
    const slides = this.#slides(track);
    const position = Math.abs(track.scrollLeft);
    let index = 0;
    let best = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs(Math.abs(this.#offset(track, slide)) - position);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });
    this.#mark(index, slides.length);
  }

  #mark(index, total) {
    this.#index = index;
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const current = Number(thumb.dataset.galleryThumb) === index;
      if (current) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${total}`;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
